import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import './fonts';
import './styles.css';
import { instalarGestosDeApp } from './app/gestosDeApp';
import { AppRoutes } from './app/routes';
import { bindQueryClient, useAuthStore } from './auth/store';
import { DemoProvider } from './demo/DemoContext';
import { ThemeProvider } from './theme/ThemeProvider';
import { ErrorBoundary } from './observability/ErrorBoundary';
import { instalarObservabilidade } from './observability/instalar';
import { instalarTelemetria } from './telemetry/instalar';
import { registerServiceWorker } from './sw/register';
import { installTokens } from './theme/tokens.css';

installTokens();
instalarGestosDeApp();
// Diagnóstico (rodada 2B1): erros globais, rejeições e 5xx viram incidentes
// sanitizados; a identidade vem da sessão, nunca do relatório.
instalarObservabilidade({ usuarioAtual: () => useAuthStore.getState().user?.id ?? null });
instalarTelemetria({ usuarioAtual: () => useAuthStore.getState().user?.id ?? null });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Em parque com rede ruim, insistir só gasta bateria. O outbox e o
      // /sync?since=seq é que garantem convergência.
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
});

// A store de sessão precisa esvaziar o cache no logout. Injetar aqui evita o
// ciclo de import que haveria se ela importasse a instância.
bindQueryClient(queryClient);

const root = document.getElementById('root');
if (!root) throw new Error('Elemento #root não encontrado.');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DemoProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
        </DemoProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Atualização por consentimento: a nova versão fica pronta e espera um toque.
// Trocar o app sob os pés de quem está registrando uma despesa é pior do que
// esperar.
if (import.meta.env.PROD) {
  registerServiceWorker({
    onUpdateAvailable: (apply: () => Promise<void>) => {
      window.dispatchEvent(new CustomEvent('nv:update-available', { detail: { apply } }));
    },
  });
}
